if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    // Shared by every instance: how many units are mid-add across the page, and
    // the real count they are all offset from.
    static pending = 0;

    static baseCartCount = 0;

    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      // Acknowledge the add before the server answers. A cart write on this
      // store takes roughly 900ms, all of it Shopify-side, so waiting for it
      // leaves the customer watching a spinner with nothing else happening.
      // The count and animation run now and are reconciled against the real
      // section HTML below, or rolled back if the add failed.
      const afterAtc = theme.settings.afterAtc;
      const submittedQty = Number(formData.get('quantity')) || 1;
      // Shared across every <product-form> on the page. A collection grid has one
      // per card and the aria-disabled guard is per form, so two quick adds can
      // be in flight at once; a per-form snapshot of the DOM count would make the
      // second read the first's optimistic value and the count would jump about.
      const previousCartCount = ProductForm.pending === 0
        ? ProductForm.readCartCount()
        : ProductForm.baseCartCount;
      ProductForm.baseCartCount = previousCartCount;

      let optimistic = false;
      let addFailed = false;
      let landed = Promise.resolve();

      if (afterAtc !== 'page') {
        optimistic = true;
        ProductForm.pending += submittedQty;

        // The flying image lands on a timer, so it can land after a failed add
        // has already put the count back. Acknowledge nothing once that happens.
        const acknowledge = () => {
          if (addFailed) return;
          ProductForm.setCartCount(ProductForm.baseCartCount + ProductForm.pending);
          ProductForm.bumpCartIcon();
          if (afterAtc === 'popup' || afterAtc === 'fly-and-popup') ProductForm.showAddedPopup();
        };

        const flight = (afterAtc === 'fly-to-cart' || afterAtc === 'fly-and-popup')
          ? this.flyToCart(acknowledge)
          : null;

        if (flight) {
          landed = flight;
        } else {
          acknowledge();
        }
      }

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Replace the optimistic count with the server's own markup, but not
          // before the flying image has arrived - the number appearing ahead of
          // it reads as a glitch. This runs outside the try/catch, so it has to
          // handle its own failure: data.sections is absent if the sections
          // request failed, and an unhandled rejection here would strand the
          // optimistic number on screen for the life of the page.
          landed.then(() => {
            ProductForm.pending = Math.max(0, ProductForm.pending - submittedQty);
            // A second add still in flight owns the count; let it reconcile.
            if (ProductForm.pending > 0) return;
            try {
              ProductForm.updateCartIcon(data);
            } catch (e) {
              console.log(e); // eslint-disable-line
              ProductForm.refreshCartCount();
            }
          });

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // This used to wait a fixed 700ms so the flying image could land
          // first. The flight now starts on click, so by the time the response
          // arrives it has usually already finished - wait on it directly
          // rather than on a timer that no longer matches anything.
          landed.then(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          });
        }

        // Whether the variant was already in the cart, derived from the add
        // response rather than a second request. data.quantity is the line's
        // NEW total, so subtracting what we just submitted gives what was there
        // before. This used to be a `GET /cart.js` awaited BEFORE the add, which
        // put a full round trip (~300ms on this store) between the customer
        // pressing the button and the add even starting.
        const submittedQty = Number(formData.get('quantity')) || 1;
        const newQuantity = Number(data.quantity);
        const oldQuantity = newQuantity - submittedQty;

        if (oldQuantity > 0) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity,
              newQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        // The add did not happen. Wait for any flight still in the air so its
        // landing cannot re-apply the count after this restores it.
        addFailed = true;
        if (optimistic) {
          landed.then(() => {
            ProductForm.pending = Math.max(0, ProductForm.pending - submittedQty);
            if (ProductForm.pending === 0) ProductForm.setCartCount(ProductForm.baseCartCount);
          });
        }
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Animates a clone of the added product's image flying to the cart icon.
     * Runs before the add request completes, so it takes no response data -
     * the image comes from the page. Returns null when it can't run (no cart
     * icon, no usable image, reduced motion), so the caller can acknowledge
     * the add some other way.
     * @param {Function} onLand - Called when the image reaches the cart icon.
     * @returns {?Promise} Resolves once the animation has landed, else null.
     */
    flyToCart(onLand) {
      const cartIcon = document.getElementById('cart-icon');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!cartIcon || reduceMotion) return null;

      // Prefer the on-screen product image nearest the button for visual
      // continuity, falling back to the image from the cart response.
      const sourceImg = this.closest('.card')?.querySelector('.card__main-image')
        || document.querySelector('#product-media img, .product-media img')
        || this.querySelector('img');
      const imageUrl = sourceImg && (sourceImg.currentSrc || sourceImg.src);
      if (!imageUrl) return null;

      const startRect = (sourceImg || this.submitBtn).getBoundingClientRect();
      if (!startRect.width || !startRect.height) return null;
      const endRect = cartIcon.getBoundingClientRect();

      const size = Math.min(startRect.width, 160);
      const startX = startRect.left + (startRect.width / 2) - (size / 2);
      const startY = startRect.top + (startRect.height / 2) - (size / 2);
      const endX = endRect.left + (endRect.width / 2) - (size / 2);
      const endY = endRect.top + (endRect.height / 2) - (size / 2);
      const dx = endX - startX;
      const dy = endY - startY;

      const clone = document.createElement('img');
      clone.src = imageUrl;
      clone.setAttribute('aria-hidden', 'true');
      Object.assign(clone.style, {
        position: 'fixed',
        left: `${startX}px`,
        top: `${startY}px`,
        width: `${size}px`,
        height: `${size}px`,
        objectFit: 'cover',
        borderRadius: '8px',
        zIndex: '1000',
        pointerEvents: 'none',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
        willChange: 'transform, opacity'
      });
      document.body.appendChild(clone);

      const animation = clone.animate([
        { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0 },
        { transform: `translate(${dx * 0.5}px, ${(dy * 0.5) - 40}px) scale(0.7)`, opacity: 1, offset: 0.5 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.15)`, opacity: 0.4, offset: 1 }
      ], {
        duration: 450,
        easing: 'cubic-bezier(0.4, 0, 0.7, 0.5)',
        fill: 'forwards'
      });

      return new Promise((resolve) => {
        let landed = false;
        const land = () => {
          if (landed) return;
          landed = true;
          clone.remove();
          onLand();
          resolve();
        };
        animation.onfinish = land;
        // Safety net in case onfinish never fires (e.g. tab backgrounded).
        setTimeout(land, 800);
      });
    }

    /**
     * Reads the quantity currently shown on the cart icon.
     * @returns {number} The displayed count, or 0 when the cart is empty.
     */
    static readCartCount() {
      const num = document.querySelector('#cart-icon-bubble [aria-hidden="true"]');
      return num ? (parseInt(num.textContent.trim(), 10) || 0) : 0;
    }

    /**
     * Writes a count to the cart icon without waiting for the server. Mirrors
     * the markup in sections/cart-icon-bubble.liquid; the real section HTML
     * replaces this as soon as the add response arrives.
     * @param {number} count - Quantity to display.
     */
    /**
     * Re-reads the cart count from the server and writes the real markup back.
     * Used when reconciliation from the add response is not possible.
     */
    static refreshCartCount() {
      fetch(`${theme.routes.cart}?sections=cart-icon-bubble`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
        .then((sections) => {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble && sections['cart-icon-bubble']) {
            bubble.innerHTML = sections['cart-icon-bubble'];
          }
        })
        .catch(() => { /* Leave the optimistic number rather than blank the badge. */ });
    }

    static setCartCount(count) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      if (count <= 0) {
        bubble.innerHTML = '';
        return;
      }
      let wrap = bubble.querySelector('.header__cart-count');
      if (!wrap) {
        bubble.innerHTML = '<div class="header__cart-count absolute bg-theme-text text-theme-bg text-center">'
          + '<span aria-hidden="true" class="header__cart-count-number"></span>'
          + '<span class="visually-hidden"></span></div>';
        wrap = bubble.querySelector('.header__cart-count');
      }
      const num = wrap.querySelector('[aria-hidden="true"]');
      if (num) num.textContent = count < 10000 ? count : '';
      const label = wrap.querySelector('.visually-hidden');
      if (label) {
        // Pick the plural form here: Liquid's `t` resolves pluralisation from the
        // count it is given, and a placeholder is not a number, so asking it for
        // a templated string always yields the "other" form ("1 items").
        const template = count === 1 ? theme.strings.cartCountOne : theme.strings.cartCountOther;
        if (template) label.textContent = template.replace('[count]', count);
      }
    }

    /**
     * Gives the cart icon a brief pulse to acknowledge the landed item.
     */
    static bumpCartIcon() {
      const cartIcon = document.getElementById('cart-icon');
      if (!cartIcon || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      cartIcon.animate([
        { transform: 'scale(1)' },
        { transform: 'scale(1.25)' },
        { transform: 'scale(1)' }
      ], { duration: 300, easing: 'ease-out' });
    }

    /**
     * Briefly reveals the "Added to cart" popup near the cart icon.
     */
    static showAddedPopup() {
      const popup = document.getElementById('cart-added-popup');
      if (!popup) return;
      clearTimeout(ProductForm.popupTimer);
      popup.classList.add('is-visible');
      const visibleFor = parseInt(popup.dataset.visibleFor, 10) || 2000;
      ProductForm.popupTimer = setTimeout(() => {
        popup.classList.remove('is-visible');
      }, visibleFor);
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
